<template>
  <div
    v-if="visible"
    :class="[
      'rounded-md p-6 flex gap-3 items-center transition-all',
      variantClasses,
      customClass
    ]"
  >
    <!-- Icon -->
    <component v-if="icon" :is="icon" :size="iconSize" :class="iconClasses" />

    <!-- Content -->
    <div class="flex-1 min-w-0">
      <!-- Title -->
      <h4 v-if="title" :class="titleClasses">
        {{ title }}
      </h4>

      <!-- Message -->
      <p :class="messageClasses">
        <slot>{{ message }}</slot>
      </p>
    </div>

    <!-- Close Button -->
    <button
      v-if="dismissible"
      @click="handleDismiss"
      :class="closeButtonClasses"
      aria-label="Close alert"
    >
      <X :size="16" />
    </button>
  </div>
</template>

<script>
export default {
  name: "Alert",
  props: {
    // Content
    title: {
      type: String,
      default: ""
    },
    message: {
      type: String,
      default: ""
    },
    // Variants
    variant: {
      type: String,
      default: "info",
      validator: (value) =>
        ["info", "success", "warning", "danger", "primary"].includes(value)
    },
    // Icon
    icon: {
      type: [Object, Function],
      default: null
    },
    iconSize: {
      type: Number,
      default: 16
    },
    // Behavior
    dismissible: {
      type: Boolean,
      default: false
    },
    visible: {
      type: Boolean,
      default: true
    },
    // Custom
    customClass: {
      type: String,
      default: ""
    }
  },
  computed: {
    variantClasses() {
      const variants = {
        info: "bg-blue-50 border border-blue-100",
        success: "bg-green-50 border border-green-100",
        warning: "bg-yellow-50 border border-yellow-100",
        danger: "bg-red-50 border border-red-100",
        primary: "bg-primary-50 border border-primary-100"
      };
      return variants[this.variant] || variants.info;
    },
    iconClasses() {
      const iconColors = {
        info: "text-blue-600 shrink-0",
        success: "text-green-600 shrink-0",
        warning: "text-yellow-600 shrink-0",
        danger: "text-red-600 shrink-0",
        primary: "text-primary-600 shrink-0"
      };
      return iconColors[this.variant] || iconColors.info;
    },
    titleClasses() {
      const titleColors = {
        info: "text-blue-900 font-bold text-sm mb-2",
        success: "text-green-900 font-bold text-sm mb-2",
        warning: "text-yellow-900 font-bold text-sm mb-2",
        danger: "text-red-900 font-bold text-sm mb-2",
        primary: "text-primary-900 font-bold text-sm mb-2"
      };
      return titleColors[this.variant] || titleColors.info;
    },
    messageClasses() {
      const messageColors = {
        info: "text-blue-800 text-lg leading-tight",
        success: "text-green-800 text-lg leading-tight",
        warning: "text-yellow-800 text-lg leading-tight font-bold",
        danger: "text-red-800 text-lg leading-tight",
        primary: "text-primary-800 text-lg leading-tight"
      };
      return messageColors[this.variant] || messageColors.info;
    },
    closeButtonClasses() {
      const closeColors = {
        info: "text-blue-400 hover:text-blue-600",
        success: "text-green-400 hover:text-green-600",
        warning: "text-yellow-400 hover:text-yellow-600",
        danger: "text-red-400 hover:text-red-600",
        primary: "text-primary-400 hover:text-primary-600"
      };
      return [
        "p-1 rounded transition-colors",
        closeColors[this.variant] || closeColors.info
      ].join(" ");
    }
  },
  methods: {
    handleDismiss() {
      this.$emit("dismiss");
    }
  }
};
</script>
